import { DownOutlined, QuestionCircleOutlined, RightOutlined, UpOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { Button, Tooltip, Space, Divider } from 'antd';
import { useConversationUnreplyCustomer } from 'hooks/business/conversation';
import { useIsOnlyOneAssistant } from 'hooks/business/isOnlyOneAssistant';
import useIsAdminViewWithMultiAssist from 'hooks/business/useIsAdminViewWithMultiAssist';
import { useGetPermission, useIsAssignConversation } from 'hooks/business/useNewPermisson';
import useModal from 'hooks/useModal';
import { HTMLAttributes, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import OnlineDataModal from './OnlineDataModal';
import { useOrgConfigStateValue } from '../../../hooks/business/useOrgConfig';
import { useSize } from 'ahooks';
import { useOrgConfigQAEnable } from 'hooks/business/useOrgConfig';
import { formatNumber } from 'utils/numberFormat';
import { useReceptionBuyer } from 'hooks/business/receptionBuyerStat';
import { FocusModeWrapper } from 'components/FocusMode/FocusModeWrapper';

const lineHeight = 54;

const Fold = styled.div<{ height: string }>`
  display: flex;
  align-items: flex-start;
  height: ${({ height }) => height};
  padding: 0 10px;
  overflow: hidden;
  transition: all 0.2s;
  background: var(--fett-chat-stats-header-bg);
  position: relative;
  z-index: 5;
  [data-color-mode='dark'] & {
    border-bottom: 1px solid var(--fett-border-default);
  }

  > div {
    flex: 1 0 0;
  }
  > span {
    height: ${lineHeight}px;
    display: flex;
    align-items: center;
  }
`;

const List = styled.ul`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 0;
  margin: 0;
  list-style: none;
  font-size: 12px;
  color: var(--fett-text-secondary);
  > li {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: ${lineHeight}px;
  }

  dl {
    margin-bottom: 0;
  }
  .convHeaderItem {
    cursor: pointer;
    > span.anticon {
      grid-column: 1;
      grid-row: span 2;
      font-size: 32px;
      display: flex;
      align-items: center;
      margin-right: 4px;
    }
    > dt {
      font-size: 14px;
      grid-column: 2;
      grid-row: 1;
      margin-bottom: -4px;
      color: var(--fett-text-primary);
    }
    > dl {
      display: flex;
      align-items: center;
      font-size: 12px;
      grid-column: 2;
      grid-row: 2;
      color: var(--fett-text-tertiary);
      white-space: nowrap;
      > span {
        margin-left: 4px;
        cursor: pointer;
      }
    }
  }
`;

const Container = styled.header`
  display: flex;
  align-items: center;
  width: 100%;
  height: ${lineHeight}px;
  background: var(--fett-chat-stats-header-bg);
  color: var(--fett-text-primary);
  [data-color-mode='dark'] & {
    border-bottom: 1px solid var(--fett-border-default);
  }
  > div {
    height: 100%;
  }
`;

export interface ReceptionStatisticsHeaderProps extends HTMLAttributes<HTMLHeadElement> {}

function ReceptionStatisticsHeaderCore(props: ReceptionStatisticsHeaderProps) {
  const { ...otherProps } = props;
  const getPermission = useGetPermission();

  const { t } = useTranslate();
  const { visible, hide, show } = useModal(false);
  const receptionBuyer = useReceptionBuyer();

  const qaEnable = useOrgConfigQAEnable();
  const isAssignConversation = useIsAssignConversation();

  // 本地未回复客户数
  const conversationUnreplyCustomer = useConversationUnreplyCustomer();
  const isOnlyOneAssistant = useIsOnlyOneAssistant();

  // 该值因服务返只考虑当天未回复, 对于前天未回复的进行回复后,存在使该值为负值情况.
  const todayUnreplyNum = receptionBuyer.untreatedCount + conversationUnreplyCustomer;
  const isAdminViewWithMultiAssist = useIsAdminViewWithMultiAssist();
  const { whiteListConfig: { SIMPLUS_CONV_STATUS = false } = {} } = useOrgConfigStateValue();

  const [fold, setFold] = useState(true);
  const listRef = useRef<HTMLUListElement>(null);
  const { height: listHeight } = useSize(listRef) || {};

  return (
    <Container {...otherProps}>
      {visible && (
        <OnlineDataModal
          visible={visible}
          title={t('scene.chatWorkspace.receptionSummary.action.openRealtimeMonitor')}
          onCancel={hide}
        />
      )}
      <div>
        <Fold height={`${fold ? lineHeight : listHeight}px`}>
          <List ref={listRef}>
            {SIMPLUS_CONV_STATUS ? null : (
              <>
                <li className="px-2">
                  <Tooltip
                    className="convHeaderItem"
                    placement="bottom"
                    title={t(
                      isAdminViewWithMultiAssist
                        ? 'inreception_tips'
                        : 'inreception_assign_to_me_tips',
                    )}
                  >
                    <dl>
                      <Space size={4}>
                        {isAdminViewWithMultiAssist
                          ? t('scene.chatWorkspace.conversationList.quickFilter.action.inReception')
                          : t('scene.chatWorkspace.conversationList.label.withAssignment', {
                              baseLabel: t(
                                'scene.chatWorkspace.conversationList.quickFilter.action.inReception',
                              ),
                              assignedToMe: t(
                                'scene.chatWorkspace.conversationList.filter.assignedToMe',
                              ),
                            })}
                        <QuestionCircleOutlined />
                      </Space>
                    </dl>
                    <dt>{formatNumber(Math.max(receptionBuyer.inReceptionCount, 0))}</dt>
                  </Tooltip>
                </li>
                <li className="px-2">
                  <Tooltip
                    className="convHeaderItem"
                    placement="bottom"
                    title={t(
                      isAdminViewWithMultiAssist ? 'unreplied_tips' : 'unreplied_assign_to_me_tips',
                    )}
                  >
                    <dl>
                      <Space size={4}>
                        {isAdminViewWithMultiAssist
                          ? t('scene.chatWorkspace.conversationList.filter.toRespond')
                          : t('scene.chatWorkspace.conversationList.label.withAssignment', {
                              baseLabel: t('scene.chatWorkspace.conversationList.filter.toRespond'),
                              assignedToMe: t(
                                'scene.chatWorkspace.conversationList.filter.assignedToMe',
                              ),
                            })}
                        <QuestionCircleOutlined />
                      </Space>
                    </dl>
                    <dt>{formatNumber(todayUnreplyNum < 0 ? 0 : todayUnreplyNum)}</dt>
                  </Tooltip>
                </li>
                {isOnlyOneAssistant ? null : (
                  <li className="px-2">
                    <Tooltip
                      className="convHeaderItem"
                      placement="bottom"
                      title={
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: isAdminViewWithMultiAssist
                              ? t(
                                  'scene.chatWorkspace.conversationList.quickFilter.tooltip.toBeAssignedForAdmin',
                                )
                              : t('scene.chatWorkspace.receptionSummary.tooltip.waitAssignCount'),
                          }}
                        />
                      }
                    >
                      <dl>
                        <Space size={4}>
                          {t('scene.chatWorkspace.receptionSummary.label.waitAssignCount')}
                          <QuestionCircleOutlined />
                        </Space>
                      </dl>
                      <dt>{formatNumber(receptionBuyer.waitAssignCount)}</dt>
                    </Tooltip>
                  </li>
                )}
                <li className="py-1">
                  <Divider type="vertical" />
                </li>
              </>
            )}
            <li className="px-2">
              <Tooltip
                className="convHeaderItem"
                title={t('scene.chatWorkspace.receptionSummary.tooltip.receptionBuyerCount')}
              >
                <dl>
                  {t('scene.chatWorkspace.receptionSummary.title.todayReception')}
                  <QuestionCircleOutlined />
                </dl>
                <dt>{formatNumber(receptionBuyer.receptionBuyerCount)}</dt>
              </Tooltip>
            </li>
            {qaEnable && isAssignConversation && (
              <li className="px-2">
                <Tooltip
                  className="convHeaderItem"
                  title={t(
                    'scene.chatWorkspace.receptionSummary.tooltip.manualReceptionBuyerCount',
                  )}
                >
                  <dl>
                    {t('scene.chatWorkspace.receptionSummary.label.manualReceptionBuyerCount')}
                    <QuestionCircleOutlined />
                  </dl>
                  <dt>{formatNumber(receptionBuyer.manualReceptionBuyerCount)}</dt>
                </Tooltip>
              </li>
            )}
            <li className="px-2">
              <Tooltip
                className="convHeaderItem"
                title={t('scene.chatWorkspace.receptionSummary.tooltip.noOrderBuyerCount')}
              >
                <dl>
                  {t('domain.order.status.notPlaced')}
                  <QuestionCircleOutlined />
                </dl>
                <dt>{formatNumber(receptionBuyer.noOrderCount)}</dt>
              </Tooltip>
            </li>
            <li className="px-2">
              <Tooltip
                className="convHeaderItem"
                title={t('scene.chatWorkspace.receptionSummary.tooltip.noPaymentBuyerCount')}
              >
                <dl>
                  {t('domain.order.status.unpaid')}
                  <QuestionCircleOutlined />
                </dl>
                <dt>{formatNumber(receptionBuyer.noPaymentCount)}</dt>
              </Tooltip>
            </li>
            <li className="px-2">
              <Tooltip
                className="convHeaderItem"
                title={t('scene.chatWorkspace.receptionSummary.tooltip.paidBuyerCount')}
              >
                <dl>
                  {t('domain.order.status.paid')}
                  <QuestionCircleOutlined />
                </dl>
                <dt>{formatNumber(receptionBuyer.paidCount)}</dt>
              </Tooltip>
            </li>
          </List>
          {listHeight && listHeight > lineHeight ? (
            <span>
              <Button type="link" onClick={() => setFold((fold) => !fold)}>
                {fold ? t('common.action.expand') : t('common.action.collapse')}
                {fold ? <DownOutlined /> : <UpOutlined />}
              </Button>
            </span>
          ) : null}
        </Fold>
      </div>
      {(getPermission('管理员视角') || getPermission('绩效报告-查看部门绩效')) && (
        <>
          <Divider type="vertical" style={{ height: 13, marginLeft: 0 }} />
          <Button
            type="link"
            className={css`
              font-size: 12px;
            `}
            size="small"
            onClick={show}
          >
            {t('scene.chatWorkspace.receptionSummary.action.openRealtimeMonitor')}
            <RightOutlined
              className={css`
                font-size: 12px;
              `}
            />
          </Button>
        </>
      )}
    </Container>
  );
}

export const ReceptionStatisticsHeader = (
  props: ReceptionStatisticsHeaderProps & { isEmpty?: boolean },
) => {
  const { isEmpty } = props;

  return (
    <FocusModeWrapper>
      <ReceptionStatisticsHeaderCore {...props} className={isEmpty ? 'grayscale' : ''} />
    </FocusModeWrapper>
  );
};
